import React from 'react';
import { Message } from './types';
import { useTranslation } from 'react-i18next';

interface StreamingIndicatorProps {
  message: Message;
}


/** Connection notices are owned by CatalogStatusStrip (page level), not the bubble. */
export const CATALOG_CONNECTION_KINDS = new Set([
  'catalog_connecting', 'catalog_connected', 'catalog_reconnecting', 'catalog_reconnected',
]);

/** Payload of a catalog status event (see catax_ai/catalogs/status_notice.py). */
export function parseCatalogStatus(content?: string): { kind: string; catalog: string; seconds: number; count: number; items: string[] } {
  try {
    const data = JSON.parse(content || '');
    if (data && typeof data === 'object') {
      return {
        kind: String(data.kind || ''),
        catalog: String(data.catalog || ''),
        seconds: Math.max(0, Number(data.seconds) || 0),
        count: Math.max(0, Number(data.count) || 0),
        items: Array.isArray(data.items) ? data.items.map(String).filter(Boolean).slice(0, 4) : [],
      };
    }
  } catch {
    /* not JSON — fall through */
  }
  return { kind: '', catalog: '', seconds: 0, count: 0, items: [] };
}

export function catalogStatusFallback(type: string, seconds: number, count = 0, items = ''): string {
  // Attachment stages. An image turn is ONE long model call, and the bubble used
  // to say "Thinking…" for all 30-60 s of it.
  if (type === 'catalog_image_read') return count > 1 ? `🔍 Reading your ${count} photos for a VIN…` : '🔍 Reading your photo for a VIN…';
  if (type === 'catalog_image_vehicle') return items ? `🚗 ${items} identified — now looking at the photo…` : '🚗 Vehicle identified — now looking at the photo…';
  if (type === 'catalog_image_analyze') return count > 1 ? `👀 Identifying the parts in your ${count} photos…` : '👀 Identifying the parts in your photo…';
  // Marked mechanical photo at deeper thinking: ~50-70 s on one call. The
  // backend re-sends this every ~25 s so the 90 s "server is busy" notice
  // never fires on a healthy call.
  if (type === 'catalog_image_analyze_deep') return '🔬 Looking closely at the parts you marked — this can take up to a minute…';
  if (type === 'catalog_doc_analyze') return count > 1 ? `📑 Reading your ${count} documents…` : '📑 Reading your document…';
  if (type === 'catalog_search_tree') return '📖 Verifying the VIN-specific vehicle configuration…';
  const subject = items || `${count} requested part(s)`;
  if (type === 'catalog_search_map') return `🗺️ Mapping ${subject} to likely diagrams…`;
  if (type === 'catalog_search_fetch') return `📄 Checking diagrams, notes, and fitment for ${subject}…`;
  if (type === 'catalog_search_resolve') return `🔎 Comparing candidates for ${subject} by position, equipment, and production data…`;
  if (type === 'catalog_search_load') return count > 0 ? `📦 Preparing OE numbers, quantities, and selection evidence for ${subject}…` : '📦 Preparing OE numbers and selection evidence…';
  if (type === 'catalog_connecting') return '⏳ Connecting to catalog…';
  if (type === 'catalog_connected') return '✅ Catalog connected';
  if (type === 'catalog_reconnecting') {
    return seconds > 0
      ? `⏳ Catalog session is busy — reconnecting in about ${seconds}s…`
      : '⏳ Catalog session is busy — reconnecting…';
  }
  if (type === 'catalog_reconnected') return '✅ Catalog reconnected';
  return type;
}

const StreamingIndicator: React.FC<StreamingIndicatorProps> = ({ message }) => {

  const { t } = useTranslation();
  const [elapsedTime, setElapsedTime] = React.useState(0);
  const startTimeRef = React.useRef<number>(Date.now());
  const lastEventCountRef = React.useRef<number>(0);
  const lastProgressTimeRef = React.useRef<number>(Date.now());
  const [stalledSeconds, setStalledSeconds] = React.useState(0);

  // Determine expected max time based on whether there's an image
  const hasImage = message.events?.some(event =>
    event.type.includes('image') || event.type.includes('annotated')
  ) || false;

  const expectedMaxTime = 180; // 180s for images, 40s for text-only

  // Elapsed timer effect
  React.useEffect(() => {
    if (message.isStreaming) {
      startTimeRef.current = Date.now();
      lastProgressTimeRef.current = Date.now();
      lastEventCountRef.current = message.events?.length ?? 0;
      setElapsedTime(0);
      setStalledSeconds(0);

      const timer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsedTime(elapsed);

        const currentCount = message.events?.length ?? 0;
        if (currentCount !== lastEventCountRef.current) {
          // New event arrived — reset stall clock
          lastEventCountRef.current = currentCount;
          lastProgressTimeRef.current = Date.now();
          setStalledSeconds(0);
        } else {
          const stalled = Math.floor((Date.now() - lastProgressTimeRef.current) / 1000);
          setStalledSeconds(stalled);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [message.isStreaming, message.id]);

  // Deduplicate events by type — keep only the latest occurrence of each type.
  // ACK events are never deduplicated (each one is a distinct status message).
  const deduplicatedEvents = React.useMemo(() => {
    if (!message.events) return [];
    const seen = new Map<string, typeof message.events[number]>();
    for (const event of message.events) {
      // Catalog status notices are shown as the headline only (see latestAck)
      // — listing them as a step too would print the same message twice.
      if (event.type?.startsWith('catalog_')) continue;
      if (event.type === 'ack') {
        // Use a unique key so every ACK is preserved.
        seen.set(`ack_${seen.size}`, event);
      } else {
        seen.set(event.type, event); // last write wins
      }
    }
    return Array.from(seen.values());
  }, [message.events]);

  // Calculate progress percentage based on elapsed time and expected max
  const progress = Math.min(100, (elapsedTime / expectedMaxTime) * 100);

  // Consider "stalled" only after 30 s without any new event arriving.
  // The previous condition (>= 8s && duplicateCount > 0) was triggering on
  // every normal request because duplicate event types are expected during
  // streaming (the agent emits multiple chunks of the same type).
  const isStalled = stalledSeconds >= 90;

  // Arrival time of each catalog status notice (keyed by its position in the
  // event list) so the "reconnecting in N s" wait counts DOWN on the client —
  // the server sends the wait once, at the start of the wait.
  const catalogArrivalRef = React.useRef<Map<string, number>>(new Map());
  const catalogRemainingSeconds = (key: string, seconds: number): number => {
    const arrivals = catalogArrivalRef.current;
    if (!arrivals.has(key)) arrivals.set(key, Date.now());
    const elapsed = Math.floor((Date.now() - (arrivals.get(key) as number)) / 1000);
    return Math.max(0, seconds - elapsed);
  };

  const translateEventType = (type: string, content?: string, eventKey?: string): string | null => {
    // ACK events carry their own human-readable content from the server —
    // render it directly instead of looking up a fixed i18n key.
    if (type === 'ack') {
      return content && content.trim() ? content.trim() : null;
    }
    // Catalog status notices (catalog_reconnecting / catalog_reconnected):
    // content is a small JSON {kind, catalog, seconds}; localise by key and
    // interpolate the wait so the user knows the catalog is reconnecting.
    if (type.startsWith('catalog_')) {
      const status = parseCatalogStatus(content);
      const seconds = eventKey ? catalogRemainingSeconds(eventKey, status.seconds) : status.seconds;
      const items = status.items.join(', ');
      return t(`streamingEvents.${type}${items ? '_named' : ''}`, {
        defaultValue: catalogStatusFallback(type, seconds, status.count, items),
        seconds,
        count: status.count,
        items: items || `${status.count}`,
        catalog: status.catalog,
      });
    }

    let result: string;
    if (type.startsWith('vision_agent') && type.endsWith('invoked')) {
      result = t('streamingEvents.vision_agent_invoked', 'Vision Agent Event');
    } else if (type.startsWith('vision_agent') && type.endsWith('completed')) {
      result = t('streamingEvents.vision_agent_completed', 'Vision Agent Event');
    } else if (type.startsWith('reports_agent') && type.endsWith('invoked')) {
      result = t('streamingEvents.reports_agent_invoked', 'Reports Agent Event');
    } else if (type.startsWith('reports_agent') && type.endsWith('completed')) {
      result = t('streamingEvents.reports_agent_completed', 'Reports Agent Event');
    } else if (type.startsWith('manuals_agent') && type.endsWith('invoked')) {
      result = t('streamingEvents.manuals_agent_invoked', 'Loading Owner\'s Manual…');
    } else if (type.startsWith('manuals_agent') && type.endsWith('completed')) {
      result = t('streamingEvents.manuals_agent_completed', 'Manual Loaded');
    } else if (type.startsWith('load_owners_manual') && type.endsWith('invoked')) {
      result = t('streamingEvents.load_owners_manual_invoked', 'Fetching Owner\'s Manual PDF…');
    } else if (type.startsWith('load_owners_manual') && type.endsWith('completed')) {
      result = t('streamingEvents.load_owners_manual_completed', 'Manual Ready');
    } else {
      result = t(`streamingEvents.${type}`, type);
    }
    // Empty string means internal process — signal with null
    return result === '' ? null : result;
  };

  // Find the latest ACK message to show in the header instead of "Thinking…".
  // A catalog status notice (reconnecting…) is header-worthy too: while the
  // catalog waits for its licence seat nothing else moves, so it must be the
  // headline, not a line buried in the step list.
  // Not memoised: the catalog countdown must re-evaluate on every 1 s tick.
  const latestAck = (() => {
    if (!message.events) return null;
    let last: (typeof message.events)[number] | null = null;
    let lastIndex = -1;
    message.events.forEach((e, i) => {
      if ((e.type === 'ack' && e.content?.trim())
          || (e.type?.startsWith('catalog_') && !CATALOG_CONNECTION_KINDS.has(e.type))) {
        last = e;
        lastIndex = i;
      }
    });
    if (!last) return null;
    const ev = last as (typeof message.events)[number];
    return ev.type === 'ack'
      ? ev.content.trim()
      : translateEventType(ev.type, ev.content, `${lastIndex}|${ev.type}`);
  })();

  return (
    <div className="mb-3 streaming-content">
      {/* Thinking header — replaced by latest ACK text when available */}
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isStalled ? 'bg-amber-400 animate-pulse' : 'bg-blue-500 animate-pulse'}`}></div>
        {/* Same type as a settled bubble (`text-message` + #333): this line
            is the agent narrating, and it becomes a normal message when the
            turn lands. At 14px/gray-700 the same sentence changed style
            mid-turn. */}
        <span className="text-message font-jakarta" style={{ color: '#333' }}>
          {isStalled
            ? t('chat.botMessage.takingLonger', { defaultValue: 'Taking longer than usual…' })
            : latestAck
            ? latestAck
            : t('chat.botMessage.thinking', { defaultValue: 'Thinking...' })}
        </span>
        {isStalled && (
          <span className="text-meta text-amber-500 ml-auto">
            {stalledSeconds}s
          </span>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all duration-500 ${isStalled ? 'bg-amber-400' : 'bg-blue-500'}`}
            style={{ width: `${Math.max(4, progress)}%` }}
          />
        </div>
      </div>

      {/* Stalled notice */}
      {isStalled && (
        <div className="mb-2 flex items-center gap-2 text-meta text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {t('chat.botMessage.stalledNotice', { defaultValue: 'Still working - server is busy it can take few minutes' })}
        </div>
      )}

      {/* Deduplicated events as checkmark steps */}
      {deduplicatedEvents.length > 0 && (
        <div className="space-y-1.5 text-meta mt-1">
          {(() => {
            const visibleEvents = deduplicatedEvents.slice(-3);
            const lastEvent = deduplicatedEvents[deduplicatedEvents.length - 1];
            const lastIsInternal = lastEvent ? translateEventType(lastEvent.type, lastEvent.content) === null : false;

            return (
              <>
                {visibleEvents.map((event, index, arr) => {
                  const globalIndex = deduplicatedEvents.length - arr.length + index;
                  const isLastEvent = globalIndex === deduplicatedEvents.length - 1;
                  const isCompleted = !isLastEvent || event.is_final;
                  const isCurrentlyStalled = isLastEvent && isStalled;
                  const isAck = event.type === 'ack';
                  const label = translateEventType(event.type, event.content);
                  if (label === null) return null; // skip internal events silently
                  // ACK events use a distinct blue-badge pill style
                  if (isAck) {
                    return (
                      <div key={`ack_${index}`} className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-blue-100 border border-blue-400 flex items-center justify-center flex-shrink-0">
                          <svg className="w-2.5 h-2.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <span className="font-medium text-blue-600 italic">{label}</span>
                      </div>
                    );
                  }
                  return (
                    <div key={event.type} className="flex items-center gap-2">
                      {isCompleted ? (
                        <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                          <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : isCurrentlyStalled ? (
                        <div className="w-4 h-4 rounded-full border-2 border-amber-400 flex-shrink-0 animate-pulse bg-amber-50" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-blue-400 flex-shrink-0 animate-pulse" />
                      )}
                      <span className={`font-medium capitalize ${isCompleted ? 'text-blue-600' : isCurrentlyStalled ? 'text-amber-600' : 'text-gray-500'}`}>
                        {label}
                      </span>
                      {isCurrentlyStalled && (
                        <span className="text-amber-400 text-xs ml-1">•••</span>
                      )}
                    </div>
                  );
                })}
                {/* When the active step is internal, show a subtle pulsing placeholder */}
                {lastIsInternal && (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0 animate-pulse" />
                    <span className="italic text-gray-400 animate-pulse">{t('chat.botMessage.processing', 'Processing…')}</span>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default StreamingIndicator;
